import React, { useState, useEffect } from 'react'
import { Loader2, Monitor, Video, QrCode, CloudSync } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import deviceService from '../services/deviceService'
import contentService from '../services/contentService'
import registrationService from '../services/registrationService'

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactElement
  color: string
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center mb-4">
          <div 
            className="rounded-full p-2 mr-3 text-white"
            style={{ backgroundColor: color }}
          >
            {icon}
          </div>
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        <p className="text-4xl font-bold">{value}</p>
      </CardContent>
    </Card>
  )
}

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalDevices: 0,
    onlineDevices: 0,
    totalContent: 0,
    activeCodes: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Load data in parallel
        const [deviceStats, contentStats, activeCodes] = await Promise.all([
          deviceService.getDeviceStats().catch(() => ({ totalDevices: 0, onlineDevices: 0, offlineDevices: 0, errorDevices: 0 })),
          contentService.getContentStats().catch(() => ({ totalContent: 0, totalSize: 0, readyContent: 0, processingContent: 0 })),
          registrationService.getActiveRegistrationCodes().catch(() => []),
        ])

        setStats({
          totalDevices: deviceStats.totalDevices,
          onlineDevices: deviceStats.onlineDevices,
          totalContent: contentStats.totalContent,
          activeCodes: activeCodes.length,
        })
      } catch (err) {
        console.error('Error loading dashboard data:', err)
        setError('Failed to load dashboard data. Please try refreshing the page.')
      } finally {
        setLoading(false)
      }
    }

    loadDashboardData()
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold">Dashboard</h1>
      
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Devices"
          value={stats.totalDevices}
          icon={<Monitor className="h-5 w-5" />}
          color="#1976d2"
        />
        <StatCard
          title="Online Devices"
          value={stats.onlineDevices}
          icon={<CloudSync className="h-5 w-5" />}
          color="#2e7d32"
        />
        <StatCard
          title="Content Items"
          value={stats.totalContent}
          icon={<Video className="h-5 w-5" />}
          color="#ed6c02"
        />
        <StatCard
          title="Active Codes"
          value={stats.activeCodes}
          icon={<QrCode className="h-5 w-5" />}
          color="#9c27b0"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              No recent activity to display.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              All systems operational.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}